import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

//* Interfaces
import { Login } from '../../interfaces/auth.interfaces';

//* Services
import { AuthService } from '../../services/auth.service';

//* Utils
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { showAlert } from 'src/app/utils/alert';
import { LoginLoadingComponent } from '../loading/loading.component';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
	//#region --------------------------------- Variables ---------------------------------

	private readonly _destroyRef = new Subject<void>();
	form!: FormGroup;
	showPassword: boolean = false;
	errorMessage?: string;
	loading: boolean = false;

	//#endregion

	//#region --------------------------------- Hooks ---------------------------------

	constructor(
		private _formBuilder: FormBuilder,
		private _authService: AuthService,
		private router: Router,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.buildForm();
	}

	ngOnDestroy(): void {
		this._destroyRef.next();
		this._destroyRef.complete();
	}

	//#endregion

	//#region --------------------------------- Accessors ---------------------------------

	get emailField() {
		return this.form.get('email');
	}

	get passwordField() {
		return this.form.get('password');
	}

	//#endregion

	//#region --------------------------------- Methods ---------------------------------

	buildForm() {
		this.form = this._formBuilder.group({
			email: ['', [Validators.required, Validators.email]],
			password: ['', [Validators.required]],
		});
	}

	togglePassword(event: MouseEvent) {
		this.showPassword = !this.showPassword;
		event.stopPropagation();
	}

	onSubmit() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		this.login(this.form.value);
	}

	private showLoadingDialog(){
		this.dialog.open(LoginLoadingComponent, {
			width: '100vw',
			height: '100vh',
			maxWidth: '100vw',
			maxHeight: '100vh',
			hasBackdrop: false,
			disableClose: true,
			closeOnNavigation: true,
		});
	}

	private hideLoadingDialog(){
		this.dialog.closeAll();
	}

	login(data: Login) {
		if(data.email.trim() === '' || data.password.trim() === '') {
			return;
		}
		this.loading = true;

		this._authService
			.login(data)
			.pipe(takeUntil(this._destroyRef))
			.subscribe({
				next: async () => {
					this.showLoadingDialog();
					await this.router.navigate(['/dashboard/home']);
					this.hideLoadingDialog();
				},
				error: (errorData: HttpErrorResponse) => {
					this.loading = false;

					const errorCode = errorData.error.code;
					switch (errorCode) {
						case 'INVALID_CREDENTIALS':
							this.errorMessage = 'Usuario o contraseña incorrectos. Verifique sus credenciales e intente nuevamente.';
							break;
						case 'USER_INACTIVE':
							this.errorMessage = 'Usuario inactivo. Por favor comuníquese con el administrador del sistema.';
							break;
						case 'TOO_MANY_FAILED_ATTEMPTS':
							this.errorMessage = undefined;
							showAlert('Ha excedido el límite de intentos de inicio de sesión (3). Por favor comuníquese con el administrador del sistema.', undefined, 'error');
							break;
						case 'PASSWORD_RESET_REQUIRED':
							this.router.navigate(['/auth/reset-password'], { state: {
								fromLogin: true,
								email: data.email
							}});
							break;
						case 'MAX_LOGIN_ATTEMPTS_EXCEEDED':
							this.errorMessage = 'Ha excedido el límite de intentos de inicio de sesión. Por favor intente nuevamente más tarde.';
							break;
						default:
							this.errorMessage = 'Ocurrió un error al iniciar sesión. Por favor intente nuevamente más tarde.';
							break;
					}
				},
			});
	}

	//#endregion
}
